/**
 * Client for pas-*-pkp hatch via AppFunctor proxy only.
 * Never call FC (fcapp.run) from the browser.
 */

import { apiClient } from './apiClient';

export type PasPkpDraft = {
  id: string;
  question: string;
  answer: string;
  notes?: string;
  feedback?: string;
};

export type PasPkpGenerateOneRequest = {
  assetId?: string;
  regionId?: string;
  sourceKpId?: string | null;
  hatchPrompt: string;
  /** Cached crop vision text from admit — skip re-vision when present. */
  regionContent?: string | null;
  regionFeedback?: string | null;
  regionIncomplete?: boolean;
  cropRectNormalized?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  variantIndex?: number;
  existingCount?: number;
  /** Existing stems so the generator can diversify / avoid repeats. */
  avoidQuestions?: string[];
  /** User-banned bad samples — stronger counterexamples for generation. */
  negativeExamples?: string[];
  /** Prefer Pro model for complex hatch; longer proxy / upstream timeout. */
  useProModel?: boolean;
  model?: string;
  /** Route AppFunctor → pas-{subject}-pkp FC. */
  subject?: string | null;
};

export type PasPkpGenerateOneResult = {
  success: boolean;
  hatchPromptUsed: string;
  draft: PasPkpDraft | null;
  feedback?: string;
  regionContent?: string;
  regionFeedback?: string;
  error?: string;
  message?: string;
};

export type PasPkpGenerateBatchRequest = PasPkpGenerateOneRequest & {
  /** Infer count from OCR/KP context (english word-list coverage). Default true. */
  autoCount?: boolean;
  count?: number | 'auto';
};

export type PasPkpGenerateBatchResult = {
  success: boolean;
  hatchPromptUsed: string;
  drafts: PasPkpDraft[];
  draft?: PasPkpDraft | null;
  feedback?: string;
  regionContent?: string;
  regionFeedback?: string;
  countPlan?: {
    requested?: number;
    produced?: number;
    inferredFrom?: string;
    wordListMode?: boolean;
    wordPoolSize?: number;
    coveragePoolSize?: number;
  } | null;
  error?: string;
  message?: string;
};

/** @deprecated Prefer generatePasPracticeDraftOne + sequential UI */
export type PasPkpGenerateRequest = PasPkpGenerateOneRequest & { count?: number };

/** @deprecated Prefer generatePasPracticeDraftOne */
export type PasPkpGenerateResult = {
  success: boolean;
  hatchPromptUsed: string;
  drafts: PasPkpDraft[];
  error?: string;
  message?: string;
};

/** AppFunctor → FC round-trip; flash 目标 <30s，留余量给冷启动. */
const PROXY_TIMEOUT_MS = 60_000;
/** Pro model + FC round-trip — allow longer wait. */
const PROXY_PRO_TIMEOUT_MS = 180_000;
/** English context batch can be many items; allow up to 10 minutes. */
const PROXY_BATCH_TIMEOUT_MS = 600_000;
const PROXY_BATCH_PRO_TIMEOUT_MS = 900_000;

function pasPkpApiKind(subject?: string | null): 'math-pkp' | 'yuwen-pkp' | 'english-pkp' {
  const raw = String(subject || '').trim().toLowerCase();
  if (!raw) return 'math-pkp';
  if (raw === 'english' || raw.includes('english') || raw.includes('英语')) return 'english-pkp';
  if (raw === 'yuwen' || raw.includes('yuwen') || raw.includes('语文') || raw.includes('chinese')) {
    return 'yuwen-pkp';
  }
  return 'math-pkp';
}

function asDraft(raw: unknown): PasPkpDraft | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const id = String(record.id || '').trim();
  const question = String(record.question || '').trim();
  const answer = String(record.answer || '').trim();
  if (!id || !question) return null;
  return {
    id,
    question,
    answer,
    notes: typeof record.notes === 'string' ? record.notes : undefined,
    feedback: typeof record.feedback === 'string' ? record.feedback : undefined,
  };
}

/** Generate exactly one Practice draft via AppFunctor. */
export async function generatePasPracticeDraftOne(
  input: PasPkpGenerateOneRequest,
): Promise<PasPkpGenerateOneResult> {
  const useProModel = input.useProModel === true
    || String(input.model || '').toLowerCase().includes('pro');
  const kind = pasPkpApiKind(input.subject);
  try {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: {
        hatchPromptUsed?: string;
        draft?: PasPkpDraft | null;
        drafts?: PasPkpDraft[];
        feedback?: string;
        regionContent?: string;
        regionFeedback?: string;
      };
      error?: string;
      message?: string;
    }>(
      `/api/pas/${kind}/generate-one`,
      {
        method: 'POST',
        auth: true,
        body: {
          assetId: input.assetId,
          regionId: input.regionId,
          sourceKpId: input.sourceKpId ?? null,
          hatchPrompt: input.hatchPrompt,
          theme: input.hatchPrompt,
          subject: input.subject || undefined,
          regionContent: input.regionContent || undefined,
          variantIndex: input.variantIndex,
          existingCount: input.existingCount,
          avoidQuestions: Array.isArray(input.avoidQuestions)
            ? input.avoidQuestions.map((q) => String(q || '').trim()).filter(Boolean).slice(0, 40)
            : [],
          negativeExamples: Array.isArray(input.negativeExamples)
            ? input.negativeExamples.map((q) => String(q || '').trim()).filter(Boolean).slice(0, 20)
            : [],
          ...(useProModel ? { useProModel: true, model: input.model || 'deepseek-v4-pro' } : {}),
        },
        timeoutMs: useProModel ? PROXY_PRO_TIMEOUT_MS : PROXY_TIMEOUT_MS,
      },
    );

    if (!payload.success) {
      return {
        success: false,
        hatchPromptUsed: String(payload.data?.hatchPromptUsed || input.hatchPrompt || ''),
        draft: null,
        feedback: payload.data?.feedback || payload.data?.regionFeedback,
        regionContent: payload.data?.regionContent,
        regionFeedback: payload.data?.regionFeedback,
        error: payload.error || 'pas_pkp_generate_one_failed',
        message: payload.message || payload.data?.feedback || payload.error || '生成失败',
      };
    }

    const draft = asDraft(payload.data?.draft)
      || (Array.isArray(payload.data?.drafts) ? asDraft(payload.data?.drafts[0]) : null);

    if (!draft) {
      return {
        success: false,
        hatchPromptUsed: String(payload.data?.hatchPromptUsed || input.hatchPrompt || ''),
        draft: null,
        feedback: payload.data?.feedback || payload.data?.regionFeedback,
        regionContent: payload.data?.regionContent,
        regionFeedback: payload.data?.regionFeedback,
        error: 'empty_draft',
        message: payload.data?.feedback || '未返回题目草稿',
      };
    }

    return {
      success: true,
      hatchPromptUsed: String(payload.data?.hatchPromptUsed || input.hatchPrompt || ''),
      draft,
      feedback: payload.data?.feedback || draft.feedback || payload.data?.regionFeedback,
      regionContent: payload.data?.regionContent,
      regionFeedback: payload.data?.regionFeedback,
    };
  } catch (error) {
    const err = error as Error & { statusCode?: number; payload?: unknown };
    const payload = (err.payload && typeof err.payload === 'object')
      ? err.payload as {
        message?: string;
        error?: string;
        data?: {
          hatchPromptUsed?: string;
          feedback?: string;
          regionFeedback?: string;
          regionContent?: string;
        };
      }
      : null;
    return {
      success: false,
      hatchPromptUsed: String(payload?.data?.hatchPromptUsed || input.hatchPrompt || ''),
      draft: null,
      feedback: payload?.data?.feedback || payload?.data?.regionFeedback,
      regionContent: payload?.data?.regionContent,
      regionFeedback: payload?.data?.regionFeedback,
      error: payload?.error || (err.statusCode ? `http_${err.statusCode}` : 'proxy_failed'),
      message: payload?.message || err.message || '生成失败',
    };
  }
}

/**
 * Context-batch generate (english): one request → full draft pack.
 * Preview on client, then confirm into practice library.
 */
export async function generatePasPracticeDraftBatch(
  input: PasPkpGenerateBatchRequest,
): Promise<PasPkpGenerateBatchResult> {
  const useProModel = input.useProModel === true
    || String(input.model || '').toLowerCase().includes('pro');
  const kind = pasPkpApiKind(input.subject);
  const autoCount = input.autoCount !== false
    && (input.count === undefined || input.count === 'auto');
  try {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: {
        hatchPromptUsed?: string;
        draft?: PasPkpDraft | null;
        drafts?: PasPkpDraft[];
        feedback?: string;
        regionContent?: string;
        regionFeedback?: string;
        countPlan?: PasPkpGenerateBatchResult['countPlan'];
      };
      error?: string;
      message?: string;
    }>(
      `/api/pas/${kind}/generate`,
      {
        method: 'POST',
        auth: true,
        body: {
          assetId: input.assetId,
          regionId: input.regionId,
          sourceKpId: input.sourceKpId ?? null,
          hatchPrompt: input.hatchPrompt,
          theme: input.hatchPrompt,
          subject: input.subject || undefined,
          regionContent: input.regionContent || undefined,
          existingCount: input.existingCount,
          avoidQuestions: Array.isArray(input.avoidQuestions)
            ? input.avoidQuestions.map((q) => String(q || '').trim()).filter(Boolean).slice(0, 40)
            : [],
          negativeExamples: Array.isArray(input.negativeExamples)
            ? input.negativeExamples.map((q) => String(q || '').trim()).filter(Boolean).slice(0, 20)
            : [],
          autoCount,
          ...(autoCount ? { count: 'auto' } : { count: input.count }),
          ...(useProModel ? { useProModel: true, model: input.model || 'deepseek-v4-pro' } : {}),
        },
        timeoutMs: useProModel ? PROXY_BATCH_PRO_TIMEOUT_MS : PROXY_BATCH_TIMEOUT_MS,
      },
    );

    if (!payload.success) {
      return {
        success: false,
        hatchPromptUsed: String(payload.data?.hatchPromptUsed || input.hatchPrompt || ''),
        drafts: [],
        draft: null,
        feedback: payload.data?.feedback || payload.data?.regionFeedback,
        regionContent: payload.data?.regionContent,
        regionFeedback: payload.data?.regionFeedback,
        countPlan: payload.data?.countPlan || null,
        error: payload.error || 'pas_pkp_generate_batch_failed',
        message: payload.message || payload.data?.feedback || payload.error || '批量生成失败',
      };
    }

    const drafts = (Array.isArray(payload.data?.drafts) ? payload.data.drafts : [])
      .map((item) => asDraft(item))
      .filter((item): item is PasPkpDraft => Boolean(item));
    const draft = asDraft(payload.data?.draft) || drafts[0] || null;

    if (!drafts.length) {
      return {
        success: false,
        hatchPromptUsed: String(payload.data?.hatchPromptUsed || input.hatchPrompt || ''),
        drafts: [],
        draft: null,
        feedback: payload.data?.feedback || payload.data?.regionFeedback,
        regionContent: payload.data?.regionContent,
        regionFeedback: payload.data?.regionFeedback,
        countPlan: payload.data?.countPlan || null,
        error: 'empty_drafts',
        message: payload.data?.feedback || '未返回题目草稿包',
      };
    }

    return {
      success: true,
      hatchPromptUsed: String(payload.data?.hatchPromptUsed || input.hatchPrompt || ''),
      drafts,
      draft,
      feedback: payload.data?.feedback || draft?.feedback || payload.data?.regionFeedback,
      regionContent: payload.data?.regionContent,
      regionFeedback: payload.data?.regionFeedback,
      countPlan: payload.data?.countPlan || null,
    };
  } catch (error) {
    const err = error as Error & { statusCode?: number; payload?: unknown };
    const payload = (err.payload && typeof err.payload === 'object')
      ? err.payload as {
        message?: string;
        error?: string;
        data?: {
          hatchPromptUsed?: string;
          feedback?: string;
          regionFeedback?: string;
          regionContent?: string;
          countPlan?: PasPkpGenerateBatchResult['countPlan'];
        };
      }
      : null;
    return {
      success: false,
      hatchPromptUsed: String(payload?.data?.hatchPromptUsed || input.hatchPrompt || ''),
      drafts: [],
      draft: null,
      feedback: payload?.data?.feedback || payload?.data?.regionFeedback,
      regionContent: payload?.data?.regionContent,
      regionFeedback: payload?.data?.regionFeedback,
      countPlan: payload?.data?.countPlan || null,
      error: payload?.error || (err.statusCode ? `http_${err.statusCode}` : 'proxy_failed'),
      message: payload?.message || err.message || '批量生成失败',
    };
  }
}

/** @deprecated Prefer sequential generatePasPracticeDraftOne or english batch */
export async function generatePasPracticeDrafts(
  input: PasPkpGenerateRequest,
): Promise<PasPkpGenerateResult> {
  const count = Math.max(1, Math.min(9, Number(input.count) || 5));
  const drafts: PasPkpDraft[] = [];
  let hatchPromptUsed = input.hatchPrompt;
  for (let i = 0; i < count; i += 1) {
    const one = await generatePasPracticeDraftOne({
      ...input,
      variantIndex: i + 1,
      existingCount: drafts.length,
      avoidQuestions: [
        ...(input.avoidQuestions || []),
        ...drafts.map((d) => d.question),
      ],
    });
    hatchPromptUsed = one.hatchPromptUsed || hatchPromptUsed;
    if (!one.success || !one.draft) {
      return {
        success: drafts.length > 0,
        hatchPromptUsed,
        drafts,
        error: one.error,
        message: one.message || (drafts.length > 0
          ? `已生成 ${drafts.length} 题后中断`
          : '生成失败'),
      };
    }
    drafts.push(one.draft);
  }
  return { success: true, hatchPromptUsed, drafts };
}

export type DrsEntranceCandidate = {
  index: number;
  entranceKind: string;
  label: string;
  approachSummary: string;
  status: 'sprouted' | 'barren' | 'unknown' | string;
  isSourceParticleMethod?: boolean;
  matchesPasAttempt?: boolean;
  alreadyGenerated?: boolean;
  evidenceRefs?: string[];
  seededFromPap?: boolean;
  expandError?: string | null;
};

export type DrsForestView = {
  id: string;
  status: string;
  sourceKpId?: string | null;
  candidateEntrances: DrsEntranceCandidate[];
  selectedEntranceIndexes: number[];
  hasMultipleSolutions?: boolean;
  reasonMarkdown?: string | null;
  evidencePack?: {
    sourceKpId?: string | null;
    sourceQuestion?: string | null;
    papMissing?: boolean;
    papImplicitSteps?: Array<{ stepOrder?: number }>;
  } | null;
  paths: Array<{
    id: string;
    entranceIndex: number;
    entranceKind: string;
    source?: string;
    papCompatible?: boolean;
    papDeviation?: string | null;
    transitions: Array<{
      id: string;
      op: string;
      summary: string;
      implicitAbility?: string | null;
      practicePoints: Array<{
        id: string;
        hatchPrompt: string;
        question?: string;
        answer?: string;
        practiceKind?: string;
      }>;
    }>;
  }>;
  entrancePracticePoints?: Array<{
    id: string;
    hatchPrompt: string;
    question?: string;
    answer?: string;
  }>;
  error?: string | null;
};

export type DrsLeafView = {
  id: string;
  question?: string;
  answer?: string;
  hatchPrompt: string;
  practiceKind?: 'entrance' | 'transition' | 'obstacle';
  pathId?: string;
  transitionId?: string;
  entranceKind?: string;
  treeRole?: 'root' | 'internal' | 'leaf';
  implicitAbilityTagId?: string;
};

export async function enumeratePasDrsForest(input: {
  assetId: string;
  regionId?: string | null;
  sourceKpId?: string | null;
  subject?: string | null;
}): Promise<{ success: boolean; forest: DrsForestView | null; autoPromote: boolean; error?: string; message?: string }> {
  try {
    const kind = pasPkpApiKind(input.subject);
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: { forest?: DrsForestView; autoPromote?: boolean };
      error?: string;
      message?: string;
    }>(`/api/pas/${kind}/drs/enumerate`, {
      method: 'POST',
      auth: true,
      body: {
        assetId: input.assetId,
        regionId: input.regionId,
        sourceKpId: input.sourceKpId,
        subject: input.subject,
      },
      timeoutMs: PROXY_PRO_TIMEOUT_MS,
    });
    return {
      success: payload?.success !== false && Boolean(payload?.data?.forest),
      forest: payload?.data?.forest || null,
      autoPromote: payload?.data?.autoPromote === true,
      error: payload?.error,
      message: payload?.message,
    };
  } catch (error) {
    const err = error as { message?: string };
    return { success: false, forest: null, autoPromote: false, error: 'proxy_failed', message: err.message };
  }
}

export async function getPasDrsForestLatest(input: {
  assetId: string;
  subject?: string | null;
  forestId?: string | null;
}): Promise<{ success: boolean; forest: DrsForestView | null; leaves: DrsLeafView[]; autoPromote: boolean }> {
  try {
    const kind = pasPkpApiKind(input.subject);
    const qs = new URLSearchParams();
    if (input.assetId) qs.set('assetId', input.assetId);
    if (input.forestId) qs.set('forestId', input.forestId);
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: { forest?: DrsForestView; leaves?: DrsLeafView[]; autoPromote?: boolean };
    }>(`/api/pas/${kind}/drs/latest?${qs.toString()}`, {
      method: 'GET',
      auth: true,
      timeoutMs: PROXY_TIMEOUT_MS,
    });
    return {
      success: payload?.success !== false,
      forest: payload?.data?.forest || null,
      leaves: Array.isArray(payload?.data?.leaves) ? payload.data.leaves : [],
      autoPromote: payload?.data?.autoPromote === true,
    };
  } catch {
    return { success: false, forest: null, leaves: [], autoPromote: false };
  }
}

export async function expandPasDrsForest(input: {
  forestId: string;
  selectedEntranceIndexes: number[];
  subject?: string | null;
  assetId?: string;
}): Promise<{
  success: boolean;
  forest: DrsForestView | null;
  leaves: DrsLeafView[];
  autoPromote: boolean;
  error?: string;
  message?: string;
}> {
  try {
    const kind = pasPkpApiKind(input.subject);
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: { forest?: DrsForestView; leaves?: DrsLeafView[]; autoPromote?: boolean };
      error?: string;
      message?: string;
    }>(`/api/pas/${kind}/drs/expand`, {
      method: 'POST',
      auth: true,
      body: {
        forestId: input.forestId,
        selectedEntranceIndexes: input.selectedEntranceIndexes,
        subject: input.subject,
        assetId: input.assetId,
      },
      timeoutMs: PROXY_BATCH_TIMEOUT_MS,
    });
    return {
      success: payload?.success !== false && Boolean(payload?.data?.forest),
      forest: payload?.data?.forest || null,
      leaves: Array.isArray(payload?.data?.leaves) ? payload.data.leaves : [],
      autoPromote: payload?.data?.autoPromote === true,
      error: payload?.error,
      message: payload?.message,
    };
  } catch (error) {
    const err = error as { message?: string };
    return {
      success: false,
      forest: null,
      leaves: [],
      autoPromote: false,
      error: 'proxy_failed',
      message: err.message,
    };
  }
}

/** Persist expand-failure marker on a path entrance (survives re-entry). */
export async function markPasDrsEntranceExpandFailed(input: {
  forestId: string;
  entranceIndex: number;
  message?: string | null;
  subject?: string | null;
}): Promise<{ success: boolean; forest: DrsForestView | null }> {
  try {
    const kind = pasPkpApiKind(input.subject);
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: { forest?: DrsForestView };
    }>(`/api/pas/${kind}/drs/mark-expand-failed`, {
      method: 'POST',
      auth: true,
      body: {
        forestId: input.forestId,
        entranceIndex: input.entranceIndex,
        message: input.message || undefined,
      },
      timeoutMs: PROXY_TIMEOUT_MS,
    });
    return {
      success: payload?.success !== false && Boolean(payload?.data?.forest),
      forest: payload?.data?.forest || null,
    };
  } catch {
    return { success: false, forest: null };
  }
}
